/**
 * AiAnalysisSettingsModal：AI 分析弹窗内容层（view + interaction）。
 * - 该层负责弹窗内部交互：设置页与报告页切换、参数编辑、预览触发、应用并退出。
 * - 该层不负责“全局唯一实例”和“跨卡片会话分发”，这些由外层 Host 统一处理。
 *
 * 与 Host 拆分的价值：
 * - 保持 UI 交互逻辑聚合在一个组件中，减少外层感知细节；
 * - 将会话生命周期编排留在 Host，避免本组件承担跨卡片上下文。
 *
 * 是否可以与 Host 合并：
 * - 可以，但会形成超大组件，难以定位问题和复用；
 * - 当前拆分在可读性、可测试性和风险隔离上更稳健。
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, notification } from 'tea-component';
import { shallowEqual, useIntl, useSelector } from 'umi';
import StartsSvg from '@/pages/datalab/assets/svg/starts.svg';
import GoBackTitle from '@/pages/datalab/components/GoBackTitle';
import PreviewPage from './PreviewPage';
import Settings from './Settings';
import {
  saveAiPrompt,
  saveAiPromptApplyConfig,
} from '@/pages/datalab/services/ai';
import SaveTemplateModal, {
  type ISaveTemplateModalSubmitPayload,
} from './SaveTemplateModal';
import {
  findPromptOptionByTemplateName,
  normalizeTemplateCategory,
} from './templateConstants';
import type { TAiPromptTemplateCategory } from '@/pages/datalab/services/ai/api';
import { useReportsState, useSettingsState } from './hooks';
import { invalidateAiPromptOptionsCache } from '../hooks/useAiPromptOptions';
import { invalidateAiPromptApplyConfigCache } from '../hooks/useAiPromptApplyConfigs';
import { CUSTOM_APPROACH_VALUE } from './editorDraft';
import {
  buildAiScopeOptions,
  convertScopeToCascadeValue,
  normalizeAnalysisScope,
} from './scope';
import { buildChartIdsForApplySave } from '../utils/applyConfigUtils';
import { cancelAiAnalysis, type IBuildAiAnalysisInputParams } from '../runAiAnalysis';
import type {
  IAiAnalysisStreamingInput,
  IAiPromptInfo,
  IAiPromptOption,
  IAiScopeTabItem,
} from '../types';
import type { IUseStreamingConversationResult } from '@dbl/core/global';
import './index.scss';

export type TAiAnalysisSettingsModalType = 'setting' | 'report';

export interface IAiAnalysisSettingsModalProps {
  visible: boolean;
  onClose: () => void;
  onPreview?: () => void;
  isEdit?: boolean;
  gameCode?: string;
  dashboardId?: string;
  activeCardId?: string;
  scopeTabs: IAiScopeTabItem[];
  promptOptions: IAiPromptOption[];
  defaultSelectedApproachId?: number | string;
  popupContainer?: HTMLElement | null;
  initialPromptInfo?: IAiPromptInfo;
  onConfirmPromptInfo?: (promptInfo: IAiPromptInfo) => void;
  reportSession: IUseStreamingConversationResult<IAiAnalysisStreamingInput>;
  onRunPreviewAnalysis: (params: IBuildAiAnalysisInputParams) => Promise<void>;
  onApplyAndExit: (promptInfo: IAiPromptInfo) => void | Promise<void>;
  /** 设置 Preview 按钮触发的埋点回调 */
  onSetAiMetricsForPreview: (promptId: string) => void;
  /** 设置 Confirm 按钮触发的埋点回调 */
  onSetAiMetricsForConfirm: (promptId: string) => void;
}

const AiAnalysisSettingsModal: React.FC<IAiAnalysisSettingsModalProps> = (props) => {
  const {
    visible,
    onClose,
    onPreview,
    isEdit = false,
    gameCode = '',
    dashboardId = '',
    activeCardId = '',
    scopeTabs,
    promptOptions,
    defaultSelectedApproachId,
    popupContainer,
    initialPromptInfo,
    onConfirmPromptInfo,
    reportSession,
    onRunPreviewAnalysis,
    onApplyAndExit,
    onSetAiMetricsForPreview,
    onSetAiMetricsForConfirm,
  } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === 'en-US';
  const currentUserId = useSelector(
    (state: any) => state?.home?.userInfo?.userid ?? '',
    shallowEqual,
  );
  const [type, setType] = useState<TAiAnalysisSettingsModalType>('setting');
  const [previewPromptInfo, setPreviewPromptInfo] = useState<IAiPromptInfo | undefined>(undefined);
  const [previewRequestParams, setPreviewRequestParams] = useState<IBuildAiAnalysisInputParams | undefined>(undefined);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  /** 「设置模板」二级弹窗显隐；与主 Modal 分离渲染，避免嵌套弹层层级问题 */
  const [saveTemplateVisible, setSaveTemplateVisible] = useState<boolean>(false);
  /** 模板保存进行中：锁定主弹窗关闭与底部按钮，防止重复提交 */
  const [isSavingTemplate, setIsSavingTemplate] = useState<boolean>(false);
  /** 「下次自动应用」保存进行中 */
  const [isSavingAutoApply, setIsSavingAutoApply] = useState<boolean>(false);
  const wasVisibleRef = useRef<boolean>(false);
  /** 列表刷新后按名称选中刚保存的模板；save 未返回 id 时兜底 */
  const pendingSavedTemplateRef = useRef<{
    name: string;
    templateCategory: TAiPromptTemplateCategory;
    promptContent: string;
    analysisContext: string;
  } | null>(null);

  const handleForceClose = useCallback(() => {
    setType('setting');
    onClose();
  }, [onClose]);

  const scopeOptionsState = useMemo(() => buildAiScopeOptions(scopeTabs, isEn), [isEn, scopeTabs]);
  const normalizedInitialScope = useMemo(
    () => normalizeAnalysisScope(initialPromptInfo?.analysisScope, scopeOptionsState.allTaskIds),
    [initialPromptInfo?.analysisScope, scopeOptionsState.allTaskIds],
  );

  const settingsState = useSettingsState({
    promptOptions,
    allTaskIds: scopeOptionsState.allTaskIds,
    defaultSelectedApproachId,
    initialPromptInfo: initialPromptInfo
      ? {
        ...initialPromptInfo,
        analysisScope: normalizedInitialScope,
      }
      : undefined,
    onPreview,
    onChangeType: setType,
    onEditConfirm: (promptInfo: IAiPromptInfo) => {
      onConfirmPromptInfo?.(promptInfo);
      // Confirm 仅作用于当前激活卡片，由外层 controller 调度对应卡片的独立会话。
      onApplyAndExit(promptInfo);
      handleForceClose();
    },
  });
  const {
    resetToInitialState,
    validateScopeBeforeSubmit,
    buildPromptInfoForConfirm,
    isConfirmLocked,
    analysisScope,
    analysisContext,
    promptContent,
    selectedApproachId,
    handleConfirm,
    setAnalysisScope,
    handleAnalysisContextChange,
    handleApproachSelectChange,
    handlePromptContentChange,
    applyPresetApproachSelection,
    options,
    scopeErrorKey,
  } = settingsState;

  /** 当前分析范围内是否有可分析图表；无图表时不发起 SSE，预览/卡片展示友好空态 */
  const hasAnalyzableCharts = useMemo(
    () => normalizeAnalysisScope(analysisScope, scopeOptionsState.allTaskIds).taskIds.length > 0,
    [analysisScope, scopeOptionsState.allTaskIds],
  );

  // 与下拉展示名一致：info.templateName 为空时回退 text，避免预填名为空导致同名校验不触发
  const resolveApproachTemplateName = useCallback(
    (option: IAiPromptOption): string =>
      (option.info.templateName || option.text || '').trim(),
    [],
  );

  /** 设置模板弹窗默认值：预置思路取列表中的 template_category / 名称，自定义思路保持空名称 + personal */
  const saveTemplateDefaults = useMemo(() => {
    if (selectedApproachId === CUSTOM_APPROACH_VALUE) {
      return {
        templateCategory: normalizeTemplateCategory(),
        templateName: '',
      };
    }
    const selectedOption = options.find(
      (option) => String(option.value) === String(selectedApproachId),
    );
    return {
      templateCategory: normalizeTemplateCategory(
        selectedOption?.info.templateCategory,
      ),
      templateName: selectedOption
        ? resolveApproachTemplateName(selectedOption)
        : '',
    };
  }, [options, resolveApproachTemplateName, selectedApproachId]);

  /** 下拉中已有思路名称，供设置模板弹窗同类型重名提示 */
  const existingAiTemplates = useMemo(
    () =>
      options
        .filter((option) => option.value !== CUSTOM_APPROACH_VALUE)
        .map((option) => ({
          name: resolveApproachTemplateName(option),
          category: normalizeTemplateCategory(option.info.templateCategory),
          userId: option.info?.userId,
        }))
        .filter((item) => Boolean(item.name)),
    [options, resolveApproachTemplateName],
  );

  // 包装 Confirm：在发起分析前设置埋点回调。
  const wrappedHandleConfirm = useCallback(async () => {
    onSetAiMetricsForConfirm(String(selectedApproachId));
    await handleConfirm();
  }, [handleConfirm, onSetAiMetricsForConfirm, selectedApproachId]);
  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      setType('setting');
      setPreviewPromptInfo(undefined);
      setPreviewRequestParams(undefined);
      setSaveTemplateVisible(false);
      resetToInitialState();
    }
    wasVisibleRef.current = visible;
  }, [resetToInitialState, visible]);
  const reportsState = useReportsState(setType, () => {
    const isStreaming = reportSession.streamingState === 'pending' || reportSession.streamingState === 'progressing';
    if (!isStreaming) {
      return;
    }
    const latestConversation = reportSession.conversations[reportSession.conversations.length - 1];
    const sessionId = latestConversation?.input?.session_id;
    const messageId = latestConversation?.messageId;
    cancelAiAnalysis({ sessionId, messageId }).catch(() => {});
    reportSession.cancelStreaming();
    reportSession.clearConversations();
  });
  const handlePreviewInModal = useCallback(async () => {
    if (!validateScopeBeforeSubmit()) {
      return;
    }
    const isCustom = selectedApproachId === CUSTOM_APPROACH_VALUE;
    const normalizedScope = normalizeAnalysisScope(analysisScope, scopeOptionsState.allTaskIds);
    const nextPromptInfo: IAiPromptInfo = {
      analysisScope: normalizedScope,
      ...(isCustom ? {} : { promptId: Number(selectedApproachId) }),
      isCustom,
    };
    const nextRequestParams: IBuildAiAnalysisInputParams = {
      taskIds: normalizedScope.taskIds,
      promptContent,
      analysisContext,
    };
    if (isCustom) {
      nextPromptInfo.customPrompt = {
        promptContent,
        analysisContext,
      };
    } else {
      // 预置方案：正文来自编辑器当前值，chat 不再单独传 promptId
      // nextRequestParams.promptId = Number(selectedApproachId);
    }
    // 在发起分析前设置埋点回调，确保 __metrics__ 到达时能上报。
    onSetAiMetricsForPreview(String(selectedApproachId));
    setPreviewPromptInfo(nextPromptInfo);
    setPreviewRequestParams(nextRequestParams);
    setType('report');
    try {
      await onRunPreviewAnalysis(nextRequestParams);
    } catch (_error) {
      // 预览失败时仍停留在报告视图，由流式会话状态统一呈现错误态。
    }
  }, [
    analysisContext,
    analysisScope,
    onRunPreviewAnalysis,
    onSetAiMetricsForPreview,
    promptContent,
    scopeOptionsState.allTaskIds,
    selectedApproachId,
    validateScopeBeforeSubmit,
  ]);

  // 模板保存进行中时不允许关闭主弹窗，避免中断请求
  const handleClose = useCallback(() => {
    if (isConfirmLocked || isSavingTemplate || isSavingAutoApply) {
      return;
    }
    handleForceClose();
  }, [handleForceClose, isConfirmLocked, isSavingAutoApply, isSavingTemplate]);

  /**
   * 「下次自动应用」：持久化 apply 配置并同步卡片 promptInfo 后先关弹窗，再在卡片侧发起分析请求。
   * 避免弹窗未关闭时与卡片 loading/流式态叠在一起；分析失败仅 toast，不再重新打开弹窗。
   */
  const handleAutoApplyNextTime = useCallback(async (): Promise<void> => {
    if (!validateScopeBeforeSubmit() || isSavingAutoApply || isApplying) {
      return;
    }
    if (!gameCode || !dashboardId || !activeCardId) {
      notification.error({
        description: formatMessage({ id: 'dbl_ai_analysis_settings_auto_apply_fail' }),
      });
      return;
    }
    const trimmedPromptContent = promptContent.trim();
    if (!trimmedPromptContent) {
      return;
    }
    const normalizedScope = normalizeAnalysisScope(
      analysisScope,
      scopeOptionsState.allTaskIds,
    );
    const chartIds = buildChartIdsForApplySave(
      normalizedScope,
      scopeOptionsState.allTaskIds,
    );
    const selectedOption = options.find(
      (option) => String(option.value) === String(selectedApproachId),
    );
    const configName = String(
      selectedOption?.info.templateName
      || selectedOption?.text
      || '',
    );
    let nextPromptInfo: IAiPromptInfo | undefined;
    setIsSavingAutoApply(true);
    try {
      await saveAiPromptApplyConfig({
        game_code: gameCode,
        dashboard_id: dashboardId,
        card_id: activeCardId,
        chart_ids: chartIds,
        name: configName,
        prompt_content: promptContent,
        analysis_context: analysisContext,
      });
      invalidateAiPromptApplyConfigCache(gameCode, dashboardId);
      onSetAiMetricsForConfirm(String(selectedApproachId));
      nextPromptInfo = await buildPromptInfoForConfirm();
      onConfirmPromptInfo?.(nextPromptInfo);
      notification.success({
        description: formatMessage({ id: 'dbl_ai_analysis_settings_auto_apply_success' }),
      });
      handleForceClose();
    } catch (_error) {
      notification.error({
        description: formatMessage({ id: 'dbl_ai_analysis_settings_auto_apply_fail' }),
      });
      return;
    } finally {
      setIsSavingAutoApply(false);
    }
    if (!nextPromptInfo) {
      return;
    }
    try {
      await Promise.resolve(onApplyAndExit(nextPromptInfo));
    } catch (_error) {
      notification.error({
        description: formatMessage({ id: 'dbl_ai_analysis_settings_auto_apply_fail' }),
      });
    }
  }, [
    activeCardId,
    analysisContext,
    analysisScope,
    buildPromptInfoForConfirm,
    dashboardId,
    formatMessage,
    gameCode,
    handleForceClose,
    isApplying,
    isSavingAutoApply,
    onApplyAndExit,
    onConfirmPromptInfo,
    onSetAiMetricsForConfirm,
    options,
    promptContent,
    scopeOptionsState.allTaskIds,
    selectedApproachId,
    validateScopeBeforeSubmit,
  ]);

  // 打开模板弹窗前校验分析范围，与预览/确定保持同一前置条件
  const handleOpenSaveTemplate = useCallback(() => {
    if (!validateScopeBeforeSubmit()) {
      return;
    }
    setSaveTemplateVisible(true);
  }, [validateScopeBeforeSubmit]);

  const handleCloseSaveTemplate = useCallback(() => {
    setSaveTemplateVisible(false);
  }, []);

  // prompts/list 刷新后：若 save 未带 id，则按保存时的名称 + 类型选中对应预置思路
  useEffect(() => {
    const pending = pendingSavedTemplateRef.current;
    if (!pending) {
      return;
    }
    const matched = findPromptOptionByTemplateName(
      pending.name,
      pending.templateCategory,
      options,
    );
    if (!matched) {
      return;
    }
    applyPresetApproachSelection(matched.value, {
      promptContent: pending.promptContent,
      analysisContext: pending.analysisContext,
    });
    pendingSavedTemplateRef.current = null;
  }, [applyPresetApproachSelection, options]);

  /**
   * 保存为 AI 分析模板：直接调用 prompts/save。
   * - is_template=1 表示模板；template_category / name_cn 为模板专属字段；
   * - 同一 game_code + dashboard_id + card_id 下后端 upsert，存在则更新；
   * - 保存成功后刷新分析思路列表，并将下拉默认切至本次保存的模板名称。
   */
  const handleSaveTemplate = useCallback(async (payload: ISaveTemplateModalSubmitPayload) => {
    if (!gameCode || !dashboardId || !activeCardId) {
      throw new Error('missing save context');
    }
    setIsSavingTemplate(true);
    try {
      const saveData = await saveAiPrompt({
        game_code: gameCode,
        dashboard_id: dashboardId,
        card_id: activeCardId,
        is_custom: payload.isCustom ? 1 : 0,
        prompt_content: payload.promptContent,
        analysis_context: payload.analysisContext,
        is_template: 1,
        template_category: payload.templateCategory,
        name_cn: payload.name,
      });
      invalidateAiPromptOptionsCache(gameCode);

      const selectionContent = {
        promptContent: payload.promptContent,
        analysisContext: payload.analysisContext,
      };
      pendingSavedTemplateRef.current = {
        name: payload.name.trim(),
        templateCategory: payload.templateCategory,
        ...selectionContent,
      };
      const savedPromptId = saveData?.id;
      // 有 id 时先切选中态；list 刷新后 useEffect 再按名称对齐，避免下拉仍显示「自定义」
      if (
        typeof savedPromptId === 'number'
        && Number.isFinite(savedPromptId)
        && savedPromptId > 0
      ) {
        applyPresetApproachSelection(String(savedPromptId), selectionContent);
      }
    } finally {
      setIsSavingTemplate(false);
    }
  }, [
    activeCardId,
    applyPresetApproachSelection,
    dashboardId,
    gameCode,
  ]);
  const handleApplyAndExit = useCallback(async () => {
    if (!previewPromptInfo || !previewRequestParams || isApplying) {
      return;
    }
    setIsApplying(true);
    try {
      const promptInfo = previewPromptInfo.isCustom
        ? await buildPromptInfoForConfirm()
        : previewPromptInfo;
      onConfirmPromptInfo?.(promptInfo);
      await Promise.resolve(onApplyAndExit(promptInfo));
      handleForceClose();
    } catch (_error) {
      // 主卡片应用失败时保持弹窗，以便用户继续调整或重试。
    } finally {
      setIsApplying(false);
    }
  }, [
    buildPromptInfoForConfirm,
    handleForceClose,
    isApplying,
    onApplyAndExit,
    onConfirmPromptInfo,
    previewPromptInfo,
    previewRequestParams,
  ]);

  const captionNode = useMemo<JSX.Element>(() => {
    if (type === 'setting') {
      return (
        <span className="dbl-ai-analysis-settings-modal__caption">
          <span className="dbl-ai-analysis-settings-modal__caption-icon">
            <img src={StartsSvg} alt="" aria-hidden />
          </span>
          <span>{formatMessage({ id: 'dbl_ai_analysis_settings_title' })}</span>
        </span>
      );
    }
    return (
      <GoBackTitle
        title={formatMessage({ id: 'dbl_ai_analysis_report_preview_title' })}
        onGoBack={reportsState.handleBackToSettings}
      />
    );
  }, [formatMessage, reportsState.handleBackToSettings, type]);

  return (
    <>
      <Modal
        visible={visible}
        caption={captionNode}
        onClose={handleClose}
        className="dbl-ai-analysis-settings-modal"
        popupContainer={popupContainer || undefined}
      >
        <Modal.Body>
          {type === 'setting' ? (
            <Settings
              onPreview={handlePreviewInModal}
              onConfirm={wrappedHandleConfirm}
              onCancel={handleClose}
              // 仅「确定」提交锁主表单；保存模板 / 下次自动应用进行中不灰置表单项
              submitting={isConfirmLocked}
              autoApplying={isSavingAutoApply}
              isEdit={isEdit}
              onAutoApplyNextTime={handleAutoApplyNextTime}
              onOpenSaveTemplate={handleOpenSaveTemplate}
              locale={locale}
              scopeOptions={scopeOptionsState.options}
              allTaskIds={scopeOptionsState.allTaskIds}
              scopeValue={convertScopeToCascadeValue(analysisScope, scopeOptionsState.chartToTabMap)}
              scopeErrorText={scopeErrorKey ? formatMessage({ id: scopeErrorKey }) : ''}
              onScopeChange={setAnalysisScope}
              options={options}
              selectedApproachId={selectedApproachId}
              promptContent={promptContent}
              analysisContext={analysisContext}
              onAnalysisContextChange={handleAnalysisContextChange}
              onApproachSelectChange={handleApproachSelectChange}
              onPromptContentChange={handlePromptContentChange}
            />
          ) : (
            <PreviewPage
              onBackToSettings={reportsState.handleBackToSettings}
              onApplyAndExit={handleApplyAndExit}
              session={reportSession}
              onRunAnalysis={handlePreviewInModal}
              applyDisabled={!previewPromptInfo || !previewRequestParams || isApplying}
              hasAnalyzableCharts={hasAnalyzableCharts}
            />
          )}
        </Modal.Body>
      </Modal>
      {/* 与主设置弹窗并列挂载，复用 popupContainer 保证层级一致 */}
      <SaveTemplateModal
        visible={saveTemplateVisible}
        submitting={isSavingTemplate}
        popupContainer={popupContainer}
        onClose={handleCloseSaveTemplate}
        onSubmit={handleSaveTemplate}
        promptContent={promptContent}
        analysisContext={analysisContext}
        isCustom={selectedApproachId === CUSTOM_APPROACH_VALUE}
        defaultTemplateCategory={saveTemplateDefaults.templateCategory}
        defaultTemplateName={saveTemplateDefaults.templateName}
        existingTemplates={existingAiTemplates}
        currentUserId={currentUserId}
      />
    </>
  );
};

export default memo(AiAnalysisSettingsModal);
